import { InvariantError } from "@validereinc/utilities";
import { useMemo } from "react";
import type {
  CurrentPageInfoProps,
  PaginationType,
} from "../../Pagination/types";

export const getRowStart = (
  dir: "forward" | "reverse",
  opts: Pick<
    CurrentPageInfoProps,
    "page" | "pageSize" | "pageTotal" | "total"
  > & { isTotalKnown: boolean }
) => {
  return dir === "forward"
    ? (opts.page - 1) * opts.pageSize + 1
    : opts.isTotalKnown
      ? opts.total! - (opts.page - 1) * opts.pageSize
      : (() => {
          throw new InvariantError(
            "Reverse pagination requires the total to be known",
            {
              code: 4001,
              getExplanation: () =>
                "Reverse pagination calculates positions from the end of the dataset, which requires knowing the total number of items.",
            }
          );
        })();
};

export const getRowEnd = (
  dir: "forward" | "reverse",
  opts: Pick<
    CurrentPageInfoProps,
    "page" | "pageSize" | "pageTotal" | "total"
  > & { isTotalKnown: boolean }
) => {
  return dir === "forward"
    ? opts.isTotalKnown
      ? Math.min(opts.page * opts.pageSize, opts.total!)
      : (opts.page - 1) * opts.pageSize + opts.pageTotal
    : opts.isTotalKnown
      ? opts.total! - Math.min(opts.page * opts.pageSize, opts.total!) + 1
      : (() => {
          throw new InvariantError(
            "Reverse pagination requires the total to be known",
            {
              code: 4001,
              getExplanation: () =>
                "Reverse pagination calculates positions from the end of the dataset, which requires knowing the total number of items.",
            }
          );
        })();
};

/**
 * A hook that paginates a list of data based on the provided configuration.
 * Does not mutate the data but returns a new copy. This is a "slice and dice" hook.
 *
 * When using with other "slice and dice" hooks, make sure to run this last for
 * optimal performance.
 *
 * @param data - data to paginate
 * @param config - configuration for the pagination
 * @returns paginated data and the pagination configuration with additional
 * details
 */
export const usePagination = <TData>(
  data: TData[] = [],
  config: PaginationType
) => {
  const paginatedData = useMemo(
    () =>
      data.slice(
        config.pageSize * (config.page - 1),
        config.pageSize * config.page
      ),
    [data, config.page, config.pageSize]
  );

  const totalPages = Math.ceil(data.length / config.pageSize);

  return {
    /** paginated data. non-mutated copy. */
    data: paginatedData,
    /** pagination configuration with additional details */
    config: {
      ...config,
      hasNextPage: config.page < totalPages,
      hasPreviousPage: config.page > 1,
    },
    total: data.length,
    totalPages,
    start: getRowStart("forward", {
      page: config.page,
      pageSize: config.pageSize,
      pageTotal: paginatedData.length,
      total: data.length,
      isTotalKnown: true, // We always know the total when we have the full data array
    }),
    end: getRowEnd("forward", {
      page: config.page,
      pageSize: config.pageSize,
      pageTotal: paginatedData.length,
      total: data.length,
      isTotalKnown: true, // We always know the total when we have the full data array
    }),
  };
};
